// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { useStore } from '@nanostores/vue'
import type { IDBPDatabase } from 'idb'
import { throttle } from 'radashi'
import type { Ref, VNodeRef } from 'vue'
import { computed, onMounted, provide, reactive, ref, watch } from 'vue'
import imageSubmit from '@/assets/images/submit.png'
import imageSubmit2x from '@/assets/images/submit@2x.png'
import imageSubmit3x from '@/assets/images/submit@3x.png'
import { isApp } from '@/assets/ts/checkApp.ts'
import scrollState from '@/assets/ts/checkScroll.ts'
import {
	createCustomEvent,
	createGAEvent,
	GaGeneralEvent,
} from '@/assets/ts/customEvent'
import instanceNotification from '@/assets/ts/notification'
import NotificationList from '@/legacy/components//NotificationList.vue'
import BsAppInstallPrompt from '@/legacy/components/AppInstallPrompt.vue'
import BsButtonNormal from '@/legacy/components/ButtonNormal.vue'
import BsButtonOutline from '@/legacy/components/ButtonOutline.vue'
import BsDialogFull from '@/legacy/components/DialogFull.vue'
import BsIconButton from '@/legacy/components/IconButton.vue'
import BsLanguageList from '@/legacy/components/LanguageList.vue'
import BsNavDropdown from '@/legacy/components/NavDropdown.vue'
import BsNavDropdownItem from '@/legacy/components/NavDropdownItem.vue'
import BsSideDrawer from '@/legacy/components/SideDrawer.vue'
import BsSlidingMenu from '@/legacy/components/SlidingMenu.vue'
import { IDBHelper } from '@/legacy/ts/IDBHelper'
import getTextFromConsole from '@/legacy/ts/getTextFromConsole'
import languageDataGenerator from '@/legacy/ts/languageDataGenerator'
import {
	addOverlayListItem,
	generateUniqueId,
	removeOverlayListItem,
} from '@/legacy/ts/overlay'
import { useBreakpoints, getCurrentGaPageName } from '@/lib/utils'
import {
	checkIsPhActivation,
	getPhLayoutType,
	isRegionSupportBuyforyou,
} from '@/lib/checkService'
import { isLogin, setIsLogin } from '@/store/userStore'
import { pageName } from '@/store/pageName'
import HeaderAuth from '@/components/HeaderAuth.vue'
import { Toaster } from '@/components/ui/sonner'
import {
	ButtonNormalTheme,
	ButtonOutlineTheme,
	ButtonSize,
} from '@/types/ButtonLegacy'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import SearchInput from '@/layouts/header/components/_SearchInput.vue'
import Sheet from '@/layouts/header/components/_Sheet.vue'
import MemberCenterCard from '@/layouts/header/components/_MemberCenterCard.vue'
import i18n from '@/lang/i18n.ts'
import {
	instanceBuyandship,
	fetchGeoRedirectRecommendation,
	fetchSettingBuyforyou,
} from '@/assets/ts/apiManagement'
import GeoRedirectModal from '@/components/GeoRedirectModal.vue'

const isLoginGlobal = useStore(isLogin)
const pageNameGlobal = useStore(pageName)

export interface HeaderProvideData {
	currentMobileNavSubmenu: string
	currentMobileNavTab: string
}

export interface IMobileTab {
	id: 'info' | 'member'
	name: string
}
export const HAS_RELOAD_KEY = 'hasReload'
const RELOAD_TRUE = 'true'
const RELOAD_FALSE = 'false'

// @ts-ignore
globalThis.__VUE_OPTIONS_API__ = true
// @ts-ignore
globalThis.__VUE_PROD_DEVTOOLS__ = true

window.BsHeaderVm = createApp({
	name: 'Header',
	components: {
		BsNavDropdown,
		BsNavDropdownItem,
		BsButtonNormal,
		BsButtonOutline,
		BsDialogFull,
		BsIconButton,
		BsSideDrawer,
		BsSlidingMenu,
		BsAppInstallPrompt,
		BsLanguageList,
		NotificationList,
		BaseButton,
		SearchInput,
		Sheet,
		MemberCenterCard,
		HeaderAuth,
		Toaster,
		GeoRedirectModal,
	},
	setup() {
		const isShowPCSHeader = computed(() => {
			return pageNameGlobal.value !== ''
		})
		const isShowPCSHomeHeader = computed(() => {
			return pageNameGlobal.value === 'home'
		})
		const notification = ref<VNodeRef | null>(null)
		const linkPrivacy = `
      ${import.meta.env.PUBLIC_ROOT_URL}
      /privacy
      ${import.meta.env.PUBLIC_SUFFIX_URL}
    `

		const { isLarge } = useBreakpoints()
		const currentUrlPath = window.location.pathname
		const isPathIndex = import.meta.env.PROD
			? currentUrlPath === '/' || currentUrlPath === '/en/'
			: currentUrlPath === '/index'
		const isShowPhLayout = computed(() => {
			return (
				checkIsPhActivation() && getPhLayoutType() !== 'original' && isPathIndex
			)
		})
		const isShowOldHeaderButtons = computed(() => {
			return !isLoginGlobal.value && !isShowPhLayout.value
		})
		const isShowNewHeaderButtons = computed(() => {
			return !isLoginGlobal.value && isShowPhLayout.value
		})

		// original
		const visible = reactive({
			mobileNav: false,
		})
		const isShowNotificationCenter = ref(false)
		const isShowCookieConsent = ref(false)
		const db = ref<IDBPDatabase | null>(null)
		const profile:
			| Ref
			| {
					first_name?: string
					last_name?: string
					full_name?: string
			  } = ref(null)
		const uuid = generateUniqueId()
		const headerProvideData = reactive<HeaderProvideData>({
			currentMobileNavSubmenu: '',
			currentMobileNavTab: 'member',
		})
		const isShowLanguageMobileDialog = ref(false)
		const isShowLanguagePopup = ref(false)
		const { HeaderContent } = window
		const isShowAuthDialog = ref(false)
		const typeSuccessDialog = ref('')
		const isNeedTriggerActionButton = ref(false)
		const isGeoRedirectEnabled = ref(false)
		const geoRedirectExpirationMinutes = ref(4320) // 72 hours
		const isShowGeoRedirectDialog = ref(false)
		const buyforyouSettings = ref<Awaited<
			ReturnType<typeof fetchSettingBuyforyou>
		> | null>(null)
		const geoRedirectData = ref(null)

		provide('headerProvideData', headerProvideData)
		provide('setCurrentMobileNavSubmenu', setCurrentMobileNavSubmenu)
		provide('resetCurrentMobileNavSubmenu', resetCurrentMobileNavSubmenu)

		const currentCountry = computed(() => {
			return window.BUYANDSHIP.settings.current_country2.id!
		})
		const currentCountryObj = computed(() => {
			return window.BUYANDSHIP.settings.all_countries[currentCountry.value]
		})
		const currentCountryName = computed(() => {
			return currentCountryObj.value.name
		})
		const isNotificationEnabled = computed(() => {
			return instanceNotification.isNotificationEnabled
		})
		const currentMenuListRightDesktop = computed(() => {
			return HeaderContent.menuListRight.filter(item => item.isShowInDesktop)
		})
		const currentMenuListMobile = computed(() => {
			return [...HeaderContent.menuListLeft, ...HeaderContent.menuListRight]
		})
		const filteredMenuListMember = computed(() => {
			return HeaderContent.menuListMember.filter(item => {
				if (item.category === 'bidding') {
					const partner = buyforyouSettings.value?.partners?.yahoo
					return (
						(partner?.web_bidding_enabled ?? partner?.bidding_enabled) === 'yes'
					)
				}
				return true
			})
		})
		const currentMemberMenuListMobile = computed(() => {
			return [
				...filteredMenuListMember.value,
				...HeaderContent.menuListMemberOthers,
			]
		})
		const avatarText = computed(() => {
			if (profile.value?.full_name) {
				return profile.value?.full_name?.slice(0, 1)
			}
			return ''
		})

		const isShowDot = computed(() => {
			return instanceNotification.isHasUnRead
		})

		const imageSubmitSrc = computed(() => {
			return imageSubmit.src
		})

		const imageSubmitSrcset = computed(() => {
			return `${imageSubmit.src} 1x, ${imageSubmit2x.src} 2x, ${imageSubmit3x.src} 3x`
		})

		/**
		 * Removes the path prefix begins with en or slash from a given string.
		 * @example
		 * getPathPrefixRemoved('/en/search') // 'search'
		 * getPathPrefixRemoved('en/search') // 'search'
		 * getPathPrefixRemoved('/search') // 'search'
		 * getPathPrefixRemoved('search') // 'search'
		 */
		function getPathPrefixRemoved(str: string) {
			return str.replace(/^\/?en\/?/, '').replace(/^\//, '')
		}

		function setRedirectWithEncodedQuery(url: string = window.location.href) {
			const parsedUrl = new URL(url)
			const { searchParams } = parsedUrl
			const originalQuery = searchParams.toString()

			if (originalQuery) {
				parsedUrl.search = ''

				const redirectValue = `${getPathPrefixRemoved(
					parsedUrl.pathname,
				)}?${originalQuery}`

				searchParams.set('redirect', redirectValue)

				window.history.pushState({}, '', parsedUrl)
			}
		}

		function setRedirectUrl() {
			const url = new URL(window.location.href)
			// edge cases
			const getRedirectUrlFromLocalStorage = () => {
				const redirectValue = localStorage.getItem('loginRedirectValue')
				if (redirectValue) {
					localStorage.removeItem('loginRedirectValue')
					return getPathPrefixRemoved(redirectValue)
				}
				return ''
			}

			// common cases (by ming)
			// we handle this because Ming doesn't have time to fix the backend code
			const getRedirectUrlFromUrlParams = () => {
				const redirectValue = url.searchParams.get('redirect')
				return redirectValue ? getPathPrefixRemoved(redirectValue) : ''
			}

			const redirectLocalStorage = getRedirectUrlFromLocalStorage()
			const redirectUrlParams = getRedirectUrlFromUrlParams()

			const redirectUrl = redirectLocalStorage || redirectUrlParams

			if (redirectUrl) {
				url.searchParams.set('redirect', redirectUrl)
				window.history.pushState({}, '', url)
			} else {
				setRedirectWithEncodedQuery()
			}
		}

		function login(obj: any) {
			hideAuthPanel()
			// reset isExposeEntranceOnly headerAuth
			createCustomEvent('reset-auth-dialog', {})
			setRedirectUrl()
			if (!import.meta.env.PROD) {
				setIsLogin(true)
			}
			createCustomEvent('member_logged_in', obj)
			visible.mobileNav = false
		}

		function logout() {
			if (!isLogin.value) {
				return
			}

			instanceBuyandship.delete('sessions/user_token').then(() => {
				if (import.meta.env.PUBLIC_MODE === 'production') {
					window.location.reload()
				}
				setIsLogin(false)
				visible.mobileNav = false
			})

			localStorage.removeItem(HAS_RELOAD_KEY)
		}
		function showPanel() {
			if (isLoginGlobal.value) return
			showAuthPanel()
		}

		function showAuthPanel() {
			isShowAuthDialog.value = true
		}

		function showSignupPanel() {
			showPanel()
		}

		function showLoginPanel() {
			showPanel()
		}

		function hidePanel() {
			// for migration
			hideAuthPanel()
		}

		function hideAuthPanel() {
			isShowAuthDialog.value = false
		}

		function toggleMobileNav() {
			createGAEvent('c_menu_f_home')
			visible.mobileNav = !visible.mobileNav
		}

		function closeCookieConsent() {
			isShowCookieConsent.value = false
			if (db.value === null) {
				return
			}

			db.value.put('CookieConsent', true, 'isClose')
		}
		function openNotificationSideDrawer() {
			isShowNotificationCenter.value = true
		}
		function openNotificationOptionPanel() {
			notification.value.showOptionPanel()
		}
		function clickMemberBlock() {
			if (!isLarge.value) {
				if (!visible.mobileNav) {
					sendGaEvent('User Dropdown Menu Expand')
				}
				toggleMobileNav()
			}
		}
		function setCurrentMobileNavSubmenu(category: string) {
			headerProvideData.currentMobileNavSubmenu = category
		}
		function resetCurrentMobileNavSubmenu() {
			headerProvideData.currentMobileNavSubmenu = ''
		}
		function setCurrentMobileNavTab(tab: string) {
			headerProvideData.currentMobileNavTab = tab
		}
		function resetCurrentMobileNavTab() {
			if (isLoginGlobal.value) {
				headerProvideData.currentMobileNavTab = 'member'
			} else {
				headerProvideData.currentMobileNavTab = 'info'
			}
		}
		function showLanguageMobileDialog() {
			isShowLanguageMobileDialog.value = true
		}
		function closeLanguageMobileDialog() {
			isShowLanguageMobileDialog.value = false
		}
		function resetLogoStatus() {
			resetCurrentMobileNavSubmenu()
			closeLanguageMobileDialog()
		}
		function showLanguagePopup() {
			isShowLanguagePopup.value = true
		}
		function hideLanguagePopup() {
			isShowLanguagePopup.value = false
		}
		function initScrollListener() {
			let lastScrollPos = 0
			const mainHeader = document.getElementById('bsHeader')!
			const mainHeaderTop = document.querySelector(
				'.bs-header-top-bar',
			)! satisfies HTMLElement
			const mainHeaderHeight = () => mainHeader?.offsetHeight
			const mainHeaderTopHeight = () => mainHeaderTop.offsetHeight
			const mainHeaderBottomHeight = () =>
				mainHeaderHeight() - mainHeaderTopHeight()
			const wordpressHeader = document.getElementById('wpadminbar') // 32px deskotp, 46px mobile
			const wordpressHeaderHeight = () =>
				wordpressHeader?.offsetHeight ? wordpressHeader.offsetHeight : 0
			if (wordpressHeader) {
				scrollState.MIN_SCROLL_DISTANCE += wordpressHeaderHeight()
				mainHeader.style.transform = `translateY(${wordpressHeaderHeight()}px)`
			}

			window.addEventListener(
				'scroll',
				throttle({ interval: 200, trailing: true }, () => {
					scrollState.currentScrollPos =
						window.scrollY || document.documentElement.scrollTop
					const scrollDistance = scrollState.currentScrollPos - lastScrollPos

					if (scrollState.currentScrollPos > lastScrollPos) {
						// Scrolling down
						if (scrollDistance < scrollState.MIN_SCROLL_DISTANCE) return

						mainHeader.style.transform = `translateY(-${
							mainHeaderHeight() +
							5 +
							(wordpressHeader ? wordpressHeaderHeight() : 0)
						}px)` // Move header up by the height of header
						scrollState.isScrollingUp = false
						scrollState.headerHeight = mainHeaderHeight()
					} else if (scrollState.currentScrollPos === 0 || !isLarge.value) {
						// Scrolling up to the top or mobile header view
						mainHeader.style.transform = wordpressHeader
							? `translateY(${wordpressHeaderHeight()}px)`
							: 'none' // Fully show the header
						scrollState.isScrollingUp = true
						scrollState.headerHeight = mainHeaderHeight()
					} else {
						// Scrolling up
						mainHeader.style.transform = `translateY(-${
							mainHeaderTopHeight() -
							(wordpressHeader ? wordpressHeaderHeight() : 0)
						}px)` // Show only the bottom part of header
						scrollState.isScrollingUp = true
						scrollState.headerHeight = mainHeaderBottomHeight()
					}
					lastScrollPos =
						scrollState.currentScrollPos <= 0 ? 0 : scrollState.currentScrollPos
				}),
				false,
			)
		}
		function closeMobileNav() {
			visible.mobileNav = false
		}

		function sendGaEvent(buttonName = '') {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				button_name: buttonName,
				page_name: getCurrentGaPageName(),
			})
		}

		function hoverMenuBlock() {
			if (isLarge.value) {
				sendGaEvent('User Dropdown Menu Expand')
			}
		}

		function clickLogin() {
			showAuthPanel()
			createGAEvent(GaGeneralEvent.ButtonClick, {
				button_name: 'Login',
				...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
			})
		}

		function clickSignUp() {
			showAuthPanel()
			createGAEvent(GaGeneralEvent.ButtonClick, {
				button_name: 'Sign Up',
				...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
			})
		}

		function clickGetStarted() {
			showAuthPanel()
			createGAEvent(GaGeneralEvent.ButtonClick, {
				button_name: 'Get Start',
				...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
			})
		}

		function setTypeSuccessDialog(type: string) {
			typeSuccessDialog.value = type
		}

		function setIsShowDownloadAppDialog(value: boolean) {
			createCustomEvent('handle_download_app_dialog', {
				value,
			})
			if (value) {
				createGAEvent(GaGeneralEvent.ButtonClick, {
					button_name: 'Download App',
					...(getCurrentGaPageName() && { page_name: getCurrentGaPageName() }),
				})
			}
		}

		new IDBHelper().openCookieDB().then(async (dbData: IDBPDatabase) => {
			db.value = dbData
			isShowCookieConsent.value = !(await db.value.get(
				'CookieConsent',
				'isClose',
			))
		})
		if (isLoginGlobal.value) {
			instanceNotification.fetchList()
		}

		function checkUserCookie() {
			const isLoggedInKey = '_bns_logged_in'
			return document.cookie.includes(isLoggedInKey)
		}

		function shouldReloadPage(
			hasFECookie: boolean,
			hasUserId: boolean,
			hasReload: string | null,
		): boolean {
			return (
				hasFECookie &&
				!hasUserId &&
				(hasReload === RELOAD_FALSE || hasReload === null)
			)
		}

		function handleCookieStatus(): void {
			const hasFECookie = checkUserCookie()
			const hasUserId = Boolean(window.BUYANDSHIP.settings.user_id)
			const hasReload = localStorage.getItem(HAS_RELOAD_KEY)

			if (!shouldReloadPage(hasFECookie, hasUserId, hasReload)) return

			localStorage.setItem(HAS_RELOAD_KEY, RELOAD_TRUE)
			window.location.reload()
		}

		async function fetchGeoRedirectConfig() {
			try {
				const { data } = await instanceBuyandship.get(
					`settings/?k[]=geo_recommended`,
				)
				if (data && data.geo_recommended) {
					const geoConfig = data.geo_recommended
					isGeoRedirectEnabled.value = geoConfig.enabled === 'yes'
					geoRedirectExpirationMinutes.value = geoConfig.expire_minutes
				}
			} catch (error) {
				console.error('Failed to fetch geo redirect config:', error)
			}
		}

		async function checkGeoRedirect() {
			const geoRedirectCookie = getCookie('geo_redirect_prompted')
			if (geoRedirectCookie) {
				return
			}

			try {
				const res = await fetchGeoRedirectRecommendation()

				if (res && res.redirect === 'yes') {
					geoRedirectData.value = res
					showGeoRedirectDialog()

					setCookie(
						'geo_redirect_prompted',
						'true',
						geoRedirectExpirationMinutes.value,
					)
				}
			} catch (error) {
				console.error('Failed to check geo redirect:', error)
			}
		}

		function showGeoRedirectDialog() {
			isShowGeoRedirectDialog.value = true
		}
		function hideGeoRedirectDialog() {
			isShowGeoRedirectDialog.value = false
		}
		function handleGeoRedirectSwitch(geoData: any) {
			const { site, language } = geoData
			const countryConfig = window.BUYANDSHIP.settings.all_countries[site]

			const targetUrl =
				site === 'hk' && language
					? countryConfig.languages[language].url
					: countryConfig.url

			window.location.href = targetUrl
		}
		function handleGeoRedirectStay() {
			hideGeoRedirectDialog()
		}
		function setCookie(name: string, value: string, expirationMinutes: number) {
			const maxAge = expirationMinutes * 60 // Convert minutes to seconds
			document.cookie = `${name}=${value};max-age=${maxAge};path=/`
		}
		function getCookie(name: string) {
			// For reference: https://stackoverflow.com/a/24103596
			const nameEQ = `${name}=`
			const ca = document.cookie.split(';')
			for (let i = 0; i < ca.length; i += 1) {
				let c = ca[i]
				while (c.charAt(0) === ' ') c = c.substring(1, c.length)
				if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
			}
			return null
		}

		onMounted(async () => {
			initScrollListener()
			handleCookieStatus()

			if (!isLoginGlobal.value && !isApp) {
				await fetchGeoRedirectConfig()
				if (isGeoRedirectEnabled.value) {
					checkGeoRedirect()
				}
			}

			if (
				isLoginGlobal.value &&
				!buyforyouSettings.value &&
				isRegionSupportBuyforyou.value
			) {
				try {
					buyforyouSettings.value = await fetchSettingBuyforyou()
				} catch (error) {
					console.error('Failed to fetch buyforyou settings:', error)
				}
			}
		})

		watch(
			() => visible.mobileNav,
			val => {
				document
					.getElementById('bsHeader')!
					.classList.toggle('mobile-header-show')
				if (val) {
					addOverlayListItem(uuid)
				} else {
					removeOverlayListItem(uuid)
					setTimeout(() => {
						resetLogoStatus()
						resetCurrentMobileNavTab()
					}, 0)
				}
			},
		)

		watch(
			() => isLoginGlobal.value,
			async () => {
				if (!isLoginGlobal.value) {
					profile.value = null
				}

				if (isLoginGlobal.value && profile.value === null) {
					profile.value = {}
					profile.value.first_name = window.BUYANDSHIP.settings.user_first_name
					profile.value.last_name = window.BUYANDSHIP.settings.user_last_name
					profile.value.full_name = window.BUYANDSHIP.settings.user_full_name
				}

				resetCurrentMobileNavTab()
			},
			{
				immediate: true,
			},
		)

		watch(
			() => isLarge.value,
			value => {
				if (value) {
					visible.mobileNav = false
				}
			},
		)

		return {
			isShowPCSHomeHeader,
			isShowPCSHeader,
			visible,
			isShowNotificationCenter,
			isShowCookieConsent,
			isLoginGlobal,
			profile,
			headerProvideData,
			isShowLanguageMobileDialog,
			isShowLanguagePopup,
			HeaderContent,
			currentCountry,
			currentCountryName,
			isNotificationEnabled,
			currentMenuListRightDesktop,
			currentMenuListMobile,
			filteredMenuListMember,
			currentMemberMenuListMobile,
			avatarText,
			isApp,
			instanceNotification,
			ButtonNormalTheme,
			ButtonOutlineTheme,
			ButtonSize,
			notification,
			linkPrivacy,
			isShowDot,
			imageSubmitSrc,
			imageSubmitSrcset,
			isNeedTriggerActionButton,
			isShowAuthDialog,
			typeSuccessDialog,
			isShowPhLayout,
			isShowOldHeaderButtons,
			isShowNewHeaderButtons,
			login,
			logout,
			showPanel,
			showSignupPanel,
			showLoginPanel,
			hidePanel,
			toggleMobileNav,
			closeCookieConsent,
			openNotificationSideDrawer,
			openNotificationOptionPanel,
			clickMemberBlock,
			setCurrentMobileNavSubmenu,
			resetCurrentMobileNavSubmenu,
			setCurrentMobileNavTab,
			showLanguageMobileDialog,
			closeLanguageMobileDialog,
			resetLogoStatus,
			showLanguagePopup,
			hideLanguagePopup,
			getTextFromConsole,
			closeMobileNav,
			pageName,
			sendGaEvent,
			hoverMenuBlock,
			clickLogin,
			clickSignUp,
			setTypeSuccessDialog,
			hideAuthPanel,
			showAuthPanel,
			clickGetStarted,
			setIsShowDownloadAppDialog,
			isShowGeoRedirectDialog,
			geoRedirectData,
			hideGeoRedirectDialog,
			handleGeoRedirectSwitch,
			handleGeoRedirectStay,
		}
	},
})
	.use(i18n)
	.use(languageDataGenerator)
	.mount('#bsHeader')
